import { createContext, useContext, useState, useEffect } from "react";

const LangContext = createContext();

export const translations = {
  en: {
    // Navbar
    nav_home: "Home",
    nav_shop: "Shop",
    nav_research: "Research",
    nav_calculator: "Calculator",
    nav_about: "About",
    nav_contact: "Contact",
    nav_login: "Login",
    nav_register: "Register",
    nav_dashboard: "Dashboard",
    nav_admin: "Admin",
    nav_cart: "Cart",
    nav_logout: "Logout",
    nav_verify: "Verify Batch",
    nav_admin_panel: "Admin Panel",
    nav_login_register: "Login / Register",
    nav_footer_tagline: "Premium Research Peptides",

    // Homepage - Hero
    hero_badge: "Science. Purity. Results.",
    hero_title_1: "Premium Quality",
    hero_title_2: "Research Compounds",
    hero_title_3: "",
    hero_subtitle: "Third-party tested, batch-verified research compounds with full COA transparency. Trusted by laboratories and research institutions worldwide.",
    hero_cta_shop: "Shop Catalog",
    hero_cta_verify: "View Lab Testing",
    stat_purity: "Purity Verified",
    stat_batches: "Batches Tested",
    stat_products: "Research Products",

    // Homepage - Trust Strip
    trust_third_party: "Third-Party Tested",
    trust_us_sourced: "U.S. Sourced",
    trust_us_sourced_sub: "From cGMP / ISO-certified manufacturing facilities",
    trust_secure_payments: "Secure Payments",
    trust_support: "Top Customer Support",

    // Homepage - Analytical Transparency
    at_badge: "Batch-Verified Documentation",
    at_title_1: "Analytical",
    at_title_2: "Transparency",
    at_subtitle: "Every Hexxium Research compound ships with batch-specific analytical documentation. Here's what the verification library holds for each lot.",
    at_view_more: "Verify & Browse All COAs",
    at_view_more_sub: "Sign in to open batch-specific COAs and independent lab verification",
    at_f1_title: "99%+ Verified Purity",
    at_f1_desc: "Every batch is third-party tested by HPLC and mass spectrometry — 99%+ purity, every time, documented on its Certificate of Analysis.",
    at_f2_title: "Precise Net Content",
    at_f2_desc: "Exact fill quantity is verified per vial, so the stated amount is the amount in your hands — measured, not estimated.",
    at_f3_title: "U.S.-Sourced Compounds",
    at_f3_desc: "Lyophilized peptides synthesized in cGMP and ISO-certified facilities, ensuring quality and stability throughout our catalog.",
    at_coa_available: "COA Available",
    at_lab_verify_available: "Direct Lab Verification",
    at_view_coa: "View COA",
    at_verify_lab: "Verify With Lab",
    at_select_conc: "Select concentration",
    at_batch: "Batch",
    at_login_required: "Please sign in to view analytical documentation",

    // Homepage - Vial Section
    vial_section_badge: "Quality Assurance",
    vial_section_title_1: "Every Vial,",
    vial_section_title_2: "Verified",
    vial_section_subtitle: "Each Hexxium Research product goes through rigorous quality checks. Click the markers to learn more.",
    vial_hover_hint: "Hover to interact",
    vial_marker_1_title: "Third-Party Tested",
    vial_marker_1_desc: "Every batch is independently tested by accredited third-party laboratories to verify compound identity, purity levels, and absence of contaminants.",
    vial_marker_2_title: "Research Use Only",
    vial_marker_2_desc: "All Hexxium Research products are sold exclusively for in-vitro laboratory and pre-clinical research purposes.",
    vial_marker_3_title: "COA Verified Purity",
    vial_marker_3_desc: "Full Certificate of Analysis documentation with detailed purity reports, HPLC data, and mass spectrometry results available for every batch.",

    // Homepage - Features
    features_badge: "Why Choose Us",
    features_title_1: "Why",
    features_title_2: "Hexxium",
    features_subtitle: "We set the standard for research-grade compound quality, transparency, and reliability.",
    feature_1_title: "Third-Party Tested",
    feature_1_desc: "Every batch undergoes rigorous testing by independent laboratories to ensure purity and quality standards.",
    feature_2_title: "COA Transparency",
    feature_2_desc: "Full Certificate of Analysis documentation available for every product batch with detailed purity reports.",
    feature_3_title: "Verified Batches",
    feature_3_desc: "Use our batch verification system to confirm authenticity and access testing documentation instantly.",
    feature_4_title: "Secure Checkout",
    feature_4_desc: "Industry-standard encryption and secure payment processing for peace of mind with every order.",
    feature_5_title: "Research Focused",
    feature_5_desc: "Curated catalog specifically designed for legitimate research applications and scientific investigation.",

    // Homepage - About Summary
    about_badge: "Who We Are",
    about_summary_title_1: "About",
    about_summary_title_2: "Hexxium",
    about_summary_title_3: "Research",
    about_summary_p1: "Hexxium Research was founded by a healthcare-driven family with more than twenty years of education and experience in clinical and medical environments. Built on integrity and scientific discipline, our mission is to support independent research laboratories and qualified research individuals with premium-quality compounds manufactured under strict quality standards.",
    about_summary_p2: "We prioritize transparency through documented Certificates of Analysis, full batch traceability, and independent third-party testing. Every product is handled with precision and care to ensure reliability and consistency.",
    about_summary_p3: "We are also committed to operational excellence. Same-day shipping on qualifying orders reflects our understanding that research timelines matter. At Hexxium Research, quality, transparency, and efficiency define who we are as a research partner.",
    about_learn_more: "Learn More About Us",
    about_card_purity: "Verified Purity",
    about_card_purity_desc: "Third-party tested with full COA documentation",
    about_card_shipping: "Same-Day Shipping",
    about_card_shipping_desc: "Qualifying orders ship the same day",
    about_card_standards: "Strict Standards",
    about_card_standards_desc: "Rigorous quality control at every step",
    about_card_traceability: "Batch Traceability",
    about_card_traceability_desc: "Full transparency on every compound",

    // Homepage - Featured Products
    featured_badge: "Featured Products",
    featured_title_1: "Featured",
    featured_title_2: "Peptides",
    featured_label: "Featured",
    coa_available: "COA Available",
    details_btn: "Details",
    view_full_details: "View Full Details",
    view_all_products: "View All Products",

    // Homepage - Batch Verification
    batch_badge: "Verify Authenticity",
    batch_title_1: "Batch",
    batch_title_2: "Verification",
    batch_subtitle: "Enter your batch or lot number to verify authenticity and access COA documentation.",
    batch_placeholder: "Enter Batch / Lot Number (e.g., HLX-202501-XXXXXX)",
    batch_verify_btn: "Verify",
    batch_verified: "Batch Verified",
    batch_product: "Product",
    batch_purity: "Purity",
    batch_test_date: "Test Date",
    batch_laboratory: "Laboratory",
    batch_download_coa: "Download COA PDF",
    batch_not_found: "Batch not found. Please check the batch ID and try again.",

    // Homepage - How It Works
    timeline_badge: "Getting Started",
    timeline_title_1: "How It",
    timeline_title_2: "Works",
    timeline_step_1_title: "Create Account",
    timeline_step_1_desc: "Register for a free research account to access our full catalog and ordering system.",
    timeline_step_2_title: "Browse Catalog",
    timeline_step_2_desc: "Explore our comprehensive selection of research-grade compounds with detailed specifications.",
    timeline_step_3_title: "Review COA",
    timeline_step_3_desc: "Access and verify Certificate of Analysis documentation for your selected products.",
    timeline_step_4_title: "Secure Checkout",
    timeline_step_4_desc: "Complete your order with our encrypted, secure payment processing system.",
    timeline_step_5_title: "Track Order",
    timeline_step_5_desc: "Monitor your shipment with real-time tracking updates to your research facility.",

    // Homepage - Research Preview
    research_preview_badge: "Knowledge Base",
    research_preview_title_1: "Research",
    research_preview_title_2: "Library",
    explore_research: "Explore Research",
    explore_all_research: "Explore All Research",

    // Homepage - CTA
    cta_title_1: "Ready to Begin Your",
    cta_title_2: "Research?",
    cta_subtitle: "Join hundreds of researchers who trust Hexxium for premium quality, verified compounds.",
    cta_register: "Create Account",
    cta_shop: "Browse Catalog",

    // Homepage - Floating Banners
    promo_title: "Get 10% Off",
    promo_subtitle: "Your first order",
    promo_desc: "Join Hexxium Research today and receive 10% off your first compound order.",
    promo_cta: "Sign Up Now",
    community_title: "Research Community",
    community_subtitle: "Connect with peers",
    community_desc: "Connect with researchers in our community. Share insights and stay updated.",
    community_cta: "Join Community",

    // Shop Page
    shop_badge: "Product Catalog",
    shop_title_1: "Research",
    shop_title_2: "Peptides",
    shop_subtitle: "Browse our comprehensive selection of research-grade peptides, each with full COA documentation and batch verification.",
    shop_search_placeholder: "Search peptides...",
    shop_filters: "Filters",
    shop_clear_all: "Clear all",
    shop_category: "Category",
    shop_all_categories: "All Categories",
    shop_featured_only: "Featured Only",
    shop_in_stock: "In Stock",
    shop_apply_filters: "Apply Filters",
    shop_no_results: "No products found matching your criteria.",
    shop_clear_filters: "Clear filters",
    shop_out_of_stock: "Out of Stock",
    shop_coa: "COA",
    shop_add: "Add",
    shop_showing: "Showing",
    shop_product: "product",
    shop_products: "products",

    // Product Page
    product_home: "Home",
    product_shop: "Shop",
    product_select_batch: "Select Batch",
    product_quantity: "Quantity",
    product_total: "Total:",
    product_add_to_cart: "Add to Cart",
    product_out_of_stock: "Out of Stock",
    product_coa_title: "Certificate of Analysis",
    product_coa_subtitle: "Batch verification and purity documentation",
    product_coa_verified: "VERIFIED",
    product_batch_id: "Batch ID",
    product_purity: "Purity",
    product_test_date: "Test Date",
    product_laboratory: "Laboratory",
    product_download_coa: "Download COA PDF",
    product_specifications: "Specifications",
    product_research_notes: "Research Notes",
    product_research_note_num: "Research Note",
    product_faq: "Frequently Asked Questions",
    product_disclaimer_title: "Research Use Only",
    product_disclaimer_text: "This product is sold exclusively for in-vitro laboratory and pre-clinical research purposes. By purchasing, you confirm that you are a qualified researcher or affiliated with a research institution.",

    // About Page
    about_page_badge: "About Us",
    about_page_title_1: "About",
    about_page_title_2: "Hexxium Research",
    about_page_subtitle: "Built on integrity, transparency, and forward-thinking health exploration.",
    about_origin_badge: "Our Origin",
    about_origin_title_1: "A Healthcare-Driven",
    about_origin_title_2: "Foundation",
    about_origin_p1: "Hexxium Research was founded by a healthcare-driven family with more than twenty years of education and experience in clinical and medical environments. After decades of working within traditional healthcare systems, our team recognized the growing demand for innovative, science-based approaches to wellness and performance.",
    about_origin_p2: "That vision led to the creation of Hexxium Research, a company built on integrity, transparency, and forward-thinking health exploration.",
    about_origin_p3: "With deep roots in conventional healthcare and an appreciation for emerging advancements, Hexxium Research embraces the evolving movement often referred to as \"biohacking\" — a term widely used in today's wellness and performance space to describe strategic, science-informed methods of optimizing human health.",
    about_origin_years: "20+ Years",
    about_origin_experience: "Education & Clinical Experience",
    about_mission_badge: "Our Mission",
    about_mission_title_1: "Supporting",
    about_mission_title_2: "Responsible",
    about_mission_title_3: "Scientific Inquiry",
    about_mission_p1: "Our purpose is to provide independent research laboratories and qualified research individuals with premium-quality peptides and research compounds manufactured under strict quality control standards. Every product is selected and handled with a commitment to purity, consistency, and documented verification.",
    about_mission_p2: "We believe that meaningful progress in health innovation depends on transparency and real scientific process. For that reason, Hexxium Research prioritizes:",
    about_mission_item_1: "Independent third-party testing",
    about_mission_item_2: "Documented Certificates of Analysis",
    about_mission_item_3: "Full batch traceability",
    about_mission_item_4: "Strict sourcing standards",
    about_mission_item_5: "Clear research labeling",
    about_mission_p3: "Our clients are able to access detailed batch information and supporting documentation because trust is built on clarity.",
    about_values_badge: "Our Commitments",
    about_values_title_1: "What",
    about_values_title_2: "Defines",
    about_values_title_3: "Us",
    about_values_subtitle: "From sourcing to fulfillment, every step reflects our commitment to maintaining high standards and long-term credibility.",
    about_value_1_title: "Third-Party Testing",
    about_value_1_desc: "Independent laboratory verification on every batch ensures documented purity and consistency.",
    about_value_2_title: "Full Batch Traceability",
    about_value_2_desc: "Detailed Certificates of Analysis and batch documentation accessible to every client.",
    about_value_3_title: "Strict Sourcing Standards",
    about_value_3_desc: "Every compound is selected and handled under rigorous quality control protocols.",
    about_value_4_title: "Same-Day Shipping",
    about_value_4_desc: "Qualifying orders ship the same day because we understand research timelines matter.",
    about_value_5_title: "Research-Grade Quality",
    about_value_5_desc: "Premium peptides manufactured under strict quality standards for reliable research outcomes.",
    about_value_6_title: "Healthcare Roots",
    about_value_6_desc: "Founded by a family with 20+ years in clinical and medical environments.",
    about_ops_badge: "Operational Excellence",
    about_ops_title_1: "Precision in",
    about_ops_title_2: "Every Step",
    about_ops_p1: "Operational precision is equally important to us. We are committed to same-day shipping on qualifying orders because we understand that research timelines matter. Efficiency, accuracy, and reliability are expected in this field.",
    about_ops_p2: "Our internal processes are designed to ensure that every order is handled carefully, packaged securely, and delivered promptly.",
    about_ops_p3: "Hexxium Research is dedicated to being a dependable and trustworthy partner in research. We focus on consistency in our compounds, precision in our logistics, and transparency in our documentation. From sourcing to fulfillment, every step reflects our commitment to maintaining high standards and long-term credibility.",
    about_closing: "Hexxium Research exists to support responsible scientific inquiry and contribute to the advancement of research-driven health solutions. Our commitment extends beyond the compounds we provide. It is a commitment to the researchers and laboratories who depend on consistency, transparency, efficiency, and excellence.",
    about_explore_catalog: "Explore Our Catalog",

    // Cart Page
    cart_title: "Shopping Cart",
    cart_empty: "Your cart is empty",
    cart_items_count: "item",
    cart_items_count_plural: "items",
    cart_in_your_cart: "in your cart",
    cart_empty_title: "Your cart is empty",
    cart_empty_desc: "Explore our catalog of research-grade peptides and add products to your cart.",
    cart_browse: "Browse Products",
    cart_batch: "Batch:",
    cart_clear: "Clear Cart",
    cart_each: "each",
    cart_order_summary: "Order Summary",
    cart_subtotal: "Subtotal",
    cart_shipping: "Shipping",
    cart_shipping_calc: "Calculated at checkout",
    cart_total: "Total",
    cart_checkout: "Proceed to Checkout",
    cart_secure_note: "Secure checkout powered by industry-standard encryption",
    cart_continue: "Continue Shopping",

    // Login Page
    login_welcome: "Welcome Back",
    login_subtitle: "Sign in to access your research account",
    login_email_label: "Email Address",
    login_password_label: "Password",
    login_forgot: "Forgot password?",
    login_submit: "Sign In",
    login_new_user: "New to Hexxium?",
    login_create_account: "Create Research Account",
    login_admin_hint: "Admin? Use admin@helix.com to access the admin panel.",

    // Register Page
    register_community_title_1: "Join the",
    register_community_title_2: "Research Community",
    register_community_desc: "Create a free account to access our full catalog of research-grade peptides and documentation.",
    register_benefit_1: "Access to full product catalog",
    register_benefit_2: "Batch verification and COA downloads",
    register_benefit_3: "Order tracking and history",
    register_benefit_4: "Save favorite products",
    register_benefit_5: "Research library access",
    register_research_note_title: "Research Use Only:",
    register_research_note: "By creating an account, you confirm that you are a qualified researcher or affiliated with a research institution. All products are intended for laboratory research purposes only.",
    register_title: "Create Account",
    register_subtitle: "Fill in your details to get started",
    register_name_label: "Full Name",
    register_email_label: "Email Address",
    register_password_label: "Password",
    register_confirm_label: "Confirm Password",
    register_terms_pre: "I agree to the",
    register_terms_of_service: "Terms of Service",
    register_terms_and: "and",
    register_privacy_policy: "Privacy Policy",
    register_terms_post: ", and confirm that all purchases are for research purposes only.",
    register_submit: "Create Account",
    register_have_account: "Already have an account?",
    register_sign_in: "Sign in",

    // Footer
    footer_desc: "Premium quality research compounds. Third-party tested with full batch transparency and COA documentation.",
    footer_newsletter_placeholder: "Enter your email",
    footer_shop_title: "Shop",
    footer_shop_all: "All Products",
    footer_shop_regenerative: "Regenerative",
    footer_shop_growth: "Growth Factors",
    footer_shop_skin: "Skin Research",
    footer_research_title: "Research",
    footer_research_library: "Research Library",
    footer_research_batch: "Batch Verification",
    footer_research_coa: "COA Documentation",
    footer_account_title: "Account",
    footer_account_login: "Login",
    footer_account_register: "Register",
    footer_account_dashboard: "Dashboard",
    footer_account_orders: "Order History",
    footer_disclaimer_title: "Research Use Only",
    footer_disclaimer_text: "All products sold by Hexxium Research are intended exclusively for in-vitro laboratory and pre-clinical research purposes. By purchasing, you confirm that you are a qualified researcher or affiliated with a research institution.",
    footer_copyright: "Hexxium Research. All rights reserved.",
    footer_terms: "Terms of Service",
    footer_privacy: "Privacy Policy",
    footer_refund: "Refund Policy",
    footer_research_only: "Research Use Only",

    // Contact Page
    contact_badge: "Get in Touch",
    contact_title_1: "Contact",
    contact_title_2: "Us",
    contact_subtitle: "Any inquiries? Feel free to contact us. We'd love to hear from you.",
    contact_form_title: "Send Us a Message",
    contact_first_name: "First Name",
    contact_last_name: "Last Name",
    contact_email: "Email Address",
    contact_phone: "Phone Number",
    contact_message: "Message",
    contact_message_placeholder: "How can we help you?",
    contact_submit: "Send Message",
    contact_sending: "Sending...",
    contact_success: "Message sent successfully! We'll get back to you soon.",
    contact_error: "Failed to send message. Please try again.",
    contact_info_title: "Contact Information",
    contact_info_email: "Email",
    contact_info_response: "Response Time",
    contact_info_response_value: "Within 24 hours",
    contact_info_research: "Research Inquiries",
    contact_info_research_value: "Product & batch questions welcome",

    // Research page
    research_badge: "Peptide Knowledge Hub",
    research_title: "Research",
    research_title_2: "Library",
    research_subtitle: "Explore our comprehensive peptide database powered by research. Search by name, mechanism, goal, or category.",
    research_search_placeholder: "Search any peptide — type a name and press Enter for AI-powered results...",
    research_tab_featured: "Featured Peptides",
    research_tab_categories: "Browse by Category",
    research_tab_learn: "Start Here",
    research_tab_all: "All Peptides",
    research_featured_title: "Featured Peptides",
    research_featured_subtitle: "The most studied and widely researched peptides in modern science.",
    research_categories_title: "Browse by Category",
    research_categories_subtitle: "Explore peptides organized by their primary research application.",
    research_learn_title: "Start Here",
    research_learn_subtitle: "New to peptide research? Start with these foundational topics.",
    research_all_title: "All Peptides",
    research_all_count: "peptides in database",
    research_all_filter: "All",
    research_view_profile: "View full profile",
    research_view_profile_short: "View profile",
    research_no_results: "No results in our database for",
    research_ai_search: "Search with AI",
    research_ai_search_desc: "Get AI-powered research info about",
    research_ai_search_full: "Search with AI Research Assistant",
    research_ai_enter_hint: "Or press Enter to search with AI",
    research_ai_loading: "Researching with AI...",
    research_ai_result: "AI Research Result",
    research_ai_disclaimer: "AI-generated research content. For educational purposes only. Not medical advice.",
    research_disclaimer_title: "Research & Educational Disclaimer",
    research_disclaimer_text: "All information presented in the Hexxium Research Library is for educational and research purposes only. Content is sourced from published research literature. Compounds discussed are strictly for in-vitro laboratory and pre-clinical research use. Always consult qualified professionals for health-related decisions.",
    research_disclaimer_source: "Based on peer-reviewed research literature and published scientific studies.",
    research_peptide_count: "peptide",
    research_peptide_count_plural: "peptides",

    // Peptide Profile
    profile_overview: "Overview",
    profile_origin: "Origin",
    profile_mechanism: "Mechanism of Action",
    profile_applications: "Potential Research Applications",
    profile_administration: "Administration Routes",
    profile_safety: "Safety Considerations",
    profile_storage: "Storage Notes",
    profile_cycling: "Cycling Overview",
    profile_stacking: "Stacking Context",
    profile_disclaimer: "For research and educational purposes only. Not medical advice.",

    // AI Assistant
    assistant_title: "Research Assistant",
    assistant_ask: "Ask a question about peptides",
    assistant_placeholder: "Ask about peptides...",
    assistant_disclaimer: "Educational purposes only. Not medical advice.",
    assistant_error: "Sorry, I couldn't process your query. Please try again.",
    assistant_suggestions: ["What is BPC-157?", "Peptides for recovery?", "How does Epitalon work?"],

    // Calculator
    calc_badge: "Smart Research Tool",
    calc_title_1: "Peptide",
    calc_title_2: "Calculator",
    calc_subtitle: "Intelligent reconstitution calculator with peptide-specific research data, dose validation, and smart recommendations.",

    // Common
    loading: "Loading...",
    error: "Error",
    close: "Close",
    back: "Back",
    next: "Next",
    submit: "Submit",
    save: "Save",
    cancel: "Cancel",
    search: "Search",
    filter: "Filter",
    sort: "Sort",
    price: "Price",
    quantity: "Quantity",
    total: "Total",
  },
  es: {
    // Navbar
    nav_home: "Inicio",
    nav_shop: "Tienda",
    nav_research: "Investigación",
    nav_calculator: "Calculadora",
    nav_about: "Nosotros",
    nav_contact: "Contacto",
    nav_login: "Iniciar Sesión",
    nav_register: "Registrarse",
    nav_dashboard: "Panel",
    nav_admin: "Admin",
    nav_cart: "Carrito",
    nav_logout: "Cerrar Sesión",
    nav_verify: "Verificar Lote",
    nav_admin_panel: "Panel Admin",
    nav_login_register: "Iniciar / Registrarse",
    nav_footer_tagline: "Péptidos de Investigación Premium",

    // Homepage - Hero
    hero_badge: "Ciencia. Pureza. Resultados.",
    hero_title_1: "Calidad Premium",
    hero_title_2: "Compuestos de Investigación",
    hero_title_3: "",
    hero_subtitle: "Compuestos de investigación verificados por terceros, con total transparencia COA. Confiados por laboratorios e instituciones de investigación en todo el mundo.",
    hero_cta_shop: "Ver Catálogo",
    hero_cta_verify: "Ver Pruebas de Laboratorio",
    stat_purity: "Pureza Verificada",
    stat_batches: "Lotes Probados",
    stat_products: "Productos de Investigación",

    // Homepage - Trust Strip
    trust_third_party: "Probado por Terceros",
    trust_us_sourced: "Origen EE. UU.",
    trust_us_sourced_sub: "De instalaciones de fabricación certificadas cGMP / ISO",
    trust_secure_payments: "Pagos Seguros",
    trust_support: "Atención al Cliente Superior",

    // Homepage - Analytical Transparency
    at_badge: "Documentación Verificada por Lote",
    at_title_1: "Transparencia",
    at_title_2: "Analítica",
    at_subtitle: "Cada compuesto de Hexxium Research incluye documentación analítica específica por lote. Esto es lo que contiene la biblioteca de verificación para cada lote.",
    at_view_more: "Verificar y Explorar los COA",
    at_view_more_sub: "Inicie sesión para abrir los COA por lote y la verificación independiente de laboratorio",
    at_f1_title: "Pureza Verificada 99%+",
    at_f1_desc: "Cada lote es analizado por terceros mediante HPLC y espectrometría de masas — pureza 99%+ en cada ocasión, documentada en su Certificado de Análisis.",
    at_f2_title: "Contenido Neto Preciso",
    at_f2_desc: "La cantidad exacta de llenado se verifica por vial, de modo que la cantidad indicada es la que recibe — medida, no estimada.",
    at_f3_title: "Compuestos de Origen Estadounidense",
    at_f3_desc: "Péptidos liofilizados sintetizados en instalaciones certificadas cGMP e ISO, garantizando calidad y estabilidad en todo nuestro catálogo.",
    at_coa_available: "COA Disponible",
    at_lab_verify_available: "Verificación Directa de Laboratorio",
    at_view_coa: "Ver COA",
    at_verify_lab: "Verificar con Laboratorio",
    at_select_conc: "Seleccione la concentración",
    at_batch: "Lote",
    at_login_required: "Inicie sesión para ver la documentación analítica",

    // Homepage - Vial Section
    vial_section_badge: "Garantía de Calidad",
    vial_section_title_1: "Cada Vial,",
    vial_section_title_2: "Verificado",
    vial_section_subtitle: "Cada producto de Hexxium Research pasa por rigurosos controles de calidad. Haga clic en los marcadores para saber más.",
    vial_hover_hint: "Pase el cursor para interactuar",
    vial_marker_1_title: "Pruebas de Terceros",
    vial_marker_1_desc: "Cada lote es probado independientemente por laboratorios acreditados para verificar la identidad del compuesto, niveles de pureza y ausencia de contaminantes.",
    vial_marker_2_title: "Solo para Investigación",
    vial_marker_2_desc: "Todos los productos de Hexxium Research se venden exclusivamente para investigación de laboratorio in-vitro y preclínica.",
    vial_marker_3_title: "Pureza Verificada COA",
    vial_marker_3_desc: "Documentación completa de Certificado de Análisis con informes detallados de pureza, datos HPLC y resultados de espectrometría de masas disponibles para cada lote.",

    // Homepage - Features
    features_badge: "Por Qué Elegirnos",
    features_title_1: "Por Qué",
    features_title_2: "Hexxium",
    features_subtitle: "Establecemos el estándar de calidad, transparencia y confiabilidad en compuestos de grado investigación.",
    feature_1_title: "Pruebas de Terceros",
    feature_1_desc: "Cada lote se somete a pruebas rigurosas por laboratorios independientes para garantizar estándares de pureza y calidad.",
    feature_2_title: "Transparencia COA",
    feature_2_desc: "Documentación completa de Certificado de Análisis disponible para cada lote de producto con informes detallados de pureza.",
    feature_3_title: "Lotes Verificados",
    feature_3_desc: "Use nuestro sistema de verificación de lotes para confirmar autenticidad y acceder a documentación de pruebas al instante.",
    feature_4_title: "Pago Seguro",
    feature_4_desc: "Cifrado estándar de la industria y procesamiento seguro de pagos para su tranquilidad en cada pedido.",
    feature_5_title: "Enfoque en Investigación",
    feature_5_desc: "Catálogo curado específicamente diseñado para aplicaciones legítimas de investigación e investigación científica.",

    // Homepage - About Summary
    about_badge: "Quiénes Somos",
    about_summary_title_1: "Sobre",
    about_summary_title_2: "Hexxium",
    about_summary_title_3: "Research",
    about_summary_p1: "Hexxium Research fue fundada por una familia con vocación sanitaria con más de veinte años de educación y experiencia en entornos clínicos y médicos. Construida sobre integridad y disciplina científica, nuestra misión es apoyar laboratorios de investigación independientes e individuos de investigación calificados con compuestos de calidad premium fabricados bajo estrictos estándares de calidad.",
    about_summary_p2: "Priorizamos la transparencia a través de Certificados de Análisis documentados, trazabilidad completa de lotes y pruebas independientes de terceros. Cada producto se maneja con precisión y cuidado para garantizar confiabilidad y consistencia.",
    about_summary_p3: "También estamos comprometidos con la excelencia operativa. El envío el mismo día en pedidos calificados refleja nuestra comprensión de que los plazos de investigación son importantes. En Hexxium Research, calidad, transparencia y eficiencia definen quiénes somos como socios de investigación.",
    about_learn_more: "Conozca Más Sobre Nosotros",
    about_card_purity: "Pureza Verificada",
    about_card_purity_desc: "Probado por terceros con documentación COA completa",
    about_card_shipping: "Envío el Mismo Día",
    about_card_shipping_desc: "Los pedidos calificados se envían el mismo día",
    about_card_standards: "Estándares Estrictos",
    about_card_standards_desc: "Control de calidad riguroso en cada paso",
    about_card_traceability: "Trazabilidad de Lotes",
    about_card_traceability_desc: "Total transparencia en cada compuesto",

    // Homepage - Featured Products
    featured_badge: "Productos Destacados",
    featured_title_1: "Péptidos",
    featured_title_2: "Destacados",
    featured_label: "Destacado",
    coa_available: "COA Disponible",
    details_btn: "Detalles",
    view_full_details: "Ver Detalles Completos",
    view_all_products: "Ver Todos los Productos",

    // Homepage - Batch Verification
    batch_badge: "Verificar Autenticidad",
    batch_title_1: "Verificación de",
    batch_title_2: "Lotes",
    batch_subtitle: "Ingrese su número de lote para verificar autenticidad y acceder a documentación COA.",
    batch_placeholder: "Ingrese Número de Lote (ej., HLX-202501-XXXXXX)",
    batch_verify_btn: "Verificar",
    batch_verified: "Lote Verificado",
    batch_product: "Producto",
    batch_purity: "Pureza",
    batch_test_date: "Fecha de Prueba",
    batch_laboratory: "Laboratorio",
    batch_download_coa: "Descargar COA PDF",
    batch_not_found: "Lote no encontrado. Por favor verifique el ID del lote e intente nuevamente.",

    // Homepage - How It Works
    timeline_badge: "Cómo Empezar",
    timeline_title_1: "Cómo",
    timeline_title_2: "Funciona",
    timeline_step_1_title: "Crear Cuenta",
    timeline_step_1_desc: "Regístrese para una cuenta de investigación gratuita para acceder a nuestro catálogo completo y sistema de pedidos.",
    timeline_step_2_title: "Explorar Catálogo",
    timeline_step_2_desc: "Explore nuestra selección completa de compuestos de grado investigación con especificaciones detalladas.",
    timeline_step_3_title: "Revisar COA",
    timeline_step_3_desc: "Acceda y verifique la documentación del Certificado de Análisis para sus productos seleccionados.",
    timeline_step_4_title: "Pago Seguro",
    timeline_step_4_desc: "Complete su pedido con nuestro sistema de procesamiento de pagos seguro y cifrado.",
    timeline_step_5_title: "Rastrear Pedido",
    timeline_step_5_desc: "Monitoree su envío con actualizaciones de seguimiento en tiempo real a su instalación de investigación.",

    // Homepage - Research Preview
    research_preview_badge: "Base de Conocimiento",
    research_preview_title_1: "Biblioteca de",
    research_preview_title_2: "Investigación",
    explore_research: "Explorar Investigación",
    explore_all_research: "Explorar Toda la Investigación",

    // Homepage - CTA
    cta_title_1: "Listo para Comenzar su",
    cta_title_2: "Investigación?",
    cta_subtitle: "Únase a cientos de investigadores que confían en Hexxium para compuestos premium verificados.",
    cta_register: "Crear Cuenta",
    cta_shop: "Ver Catálogo",

    // Homepage - Floating Banners
    promo_title: "Obtenga 10% de Descuento",
    promo_subtitle: "En su primer pedido",
    promo_desc: "Únase a Hexxium Research hoy y reciba 10% de descuento en su primer pedido de compuestos.",
    promo_cta: "Regístrese Ahora",
    community_title: "Comunidad de Investigación",
    community_subtitle: "Conéctese con colegas",
    community_desc: "Conéctese con investigadores en nuestra comunidad. Comparta conocimientos y manténgase actualizado.",
    community_cta: "Unirse a la Comunidad",

    // Shop Page
    shop_badge: "Catálogo de Productos",
    shop_title_1: "Péptidos de",
    shop_title_2: "Investigación",
    shop_subtitle: "Explore nuestra selección completa de péptidos de grado investigación, cada uno con documentación COA completa y verificación de lotes.",
    shop_search_placeholder: "Buscar péptidos...",
    shop_filters: "Filtros",
    shop_clear_all: "Limpiar todo",
    shop_category: "Categoría",
    shop_all_categories: "Todas las Categorías",
    shop_featured_only: "Solo Destacados",
    shop_in_stock: "En Stock",
    shop_apply_filters: "Aplicar Filtros",
    shop_no_results: "No se encontraron productos que coincidan con sus criterios.",
    shop_clear_filters: "Limpiar filtros",
    shop_out_of_stock: "Agotado",
    shop_coa: "COA",
    shop_add: "Agregar",
    shop_showing: "Mostrando",
    shop_product: "producto",
    shop_products: "productos",

    // Product Page
    product_home: "Inicio",
    product_shop: "Tienda",
    product_select_batch: "Seleccionar Lote",
    product_quantity: "Cantidad",
    product_total: "Total:",
    product_add_to_cart: "Agregar al Carrito",
    product_out_of_stock: "Agotado",
    product_coa_title: "Certificado de Análisis",
    product_coa_subtitle: "Verificación de lote y documentación de pureza",
    product_coa_verified: "VERIFICADO",
    product_batch_id: "ID de Lote",
    product_purity: "Pureza",
    product_test_date: "Fecha de Prueba",
    product_laboratory: "Laboratorio",
    product_download_coa: "Descargar COA PDF",
    product_specifications: "Especificaciones",
    product_research_notes: "Notas de Investigación",
    product_research_note_num: "Nota de Investigación",
    product_faq: "Preguntas Frecuentes",
    product_disclaimer_title: "Solo para Investigación",
    product_disclaimer_text: "Este producto se vende exclusivamente para investigación de laboratorio in-vitro y preclínica. Al comprar, usted confirma que es un investigador calificado o está afiliado a una institución de investigación.",

    // About Page
    about_page_badge: "Sobre Nosotros",
    about_page_title_1: "Sobre",
    about_page_title_2: "Hexxium Research",
    about_page_subtitle: "Construida sobre integridad, transparencia y exploración de salud vanguardista.",
    about_origin_badge: "Nuestro Origen",
    about_origin_title_1: "Una Base Impulsada por la",
    about_origin_title_2: "Salud",
    about_origin_p1: "Hexxium Research fue fundada por una familia con vocación sanitaria con más de veinte años de educación y experiencia en entornos clínicos y médicos. Después de décadas trabajando dentro de sistemas de salud tradicionales, nuestro equipo reconoció la creciente demanda de enfoques innovadores basados en la ciencia para el bienestar y el rendimiento.",
    about_origin_p2: "Esa visión llevó a la creación de Hexxium Research, una empresa construida sobre integridad, transparencia y exploración de salud vanguardista.",
    about_origin_p3: "Con raíces profundas en la atención médica convencional y una apreciación por los avances emergentes, Hexxium Research abraza el movimiento en evolución a menudo conocido como \"biohacking\" — un término ampliamente utilizado en el espacio actual de bienestar y rendimiento para describir métodos estratégicos e informados por la ciencia para optimizar la salud humana.",
    about_origin_years: "20+ Años",
    about_origin_experience: "Experiencia Clínica",
    about_mission_badge: "Nuestra Misión",
    about_mission_title_1: "Apoyando la",
    about_mission_title_2: "Investigación Científica",
    about_mission_title_3: "Responsable",
    about_mission_p1: "Nuestro propósito es proporcionar a laboratorios de investigación independientes e individuos de investigación calificados péptidos de calidad premium y compuestos de investigación fabricados bajo estrictos estándares de control de calidad. Cada producto es seleccionado y manejado con un compromiso de pureza, consistencia y verificación documentada.",
    about_mission_p2: "Creemos que el progreso significativo en innovación de salud depende de la transparencia y el proceso científico real. Por esa razón, Hexxium Research prioriza:",
    about_mission_item_1: "Pruebas independientes de terceros",
    about_mission_item_2: "Certificados de Análisis documentados",
    about_mission_item_3: "Trazabilidad completa de lotes",
    about_mission_item_4: "Estándares estrictos de abastecimiento",
    about_mission_item_5: "Etiquetado claro de investigación",
    about_mission_p3: "Nuestros clientes pueden acceder a información detallada de lotes y documentación de apoyo porque la confianza se construye sobre la claridad.",
    about_values_badge: "Nuestros Compromisos",
    about_values_title_1: "Lo Que Nos",
    about_values_title_2: "Define",
    about_values_title_3: "",
    about_values_subtitle: "Desde el abastecimiento hasta el cumplimiento, cada paso refleja nuestro compromiso de mantener altos estándares y credibilidad a largo plazo.",
    about_value_1_title: "Pruebas de Terceros",
    about_value_1_desc: "La verificación de laboratorio independiente en cada lote garantiza pureza documentada y consistencia.",
    about_value_2_title: "Trazabilidad Completa de Lotes",
    about_value_2_desc: "Certificados de Análisis detallados y documentación de lotes accesible para cada cliente.",
    about_value_3_title: "Estándares Estrictos de Abastecimiento",
    about_value_3_desc: "Cada compuesto es seleccionado y manejado bajo protocolos rigurosos de control de calidad.",
    about_value_4_title: "Envío el Mismo Día",
    about_value_4_desc: "Los pedidos calificados se envían el mismo día porque entendemos que los plazos de investigación importan.",
    about_value_5_title: "Calidad de Grado Investigación",
    about_value_5_desc: "Péptidos premium fabricados bajo estrictos estándares de calidad para resultados de investigación confiables.",
    about_value_6_title: "Raíces en la Salud",
    about_value_6_desc: "Fundada por una familia con más de 20 años en entornos clínicos y médicos.",
    about_ops_badge: "Excelencia Operativa",
    about_ops_title_1: "Precisión en",
    about_ops_title_2: "Cada Paso",
    about_ops_p1: "La precisión operativa es igualmente importante para nosotros. Estamos comprometidos con el envío el mismo día en pedidos calificados porque entendemos que los plazos de investigación importan. Eficiencia, precisión y confiabilidad son esperadas en este campo.",
    about_ops_p2: "Nuestros procesos internos están diseñados para garantizar que cada pedido se maneje cuidadosamente, se empaque de forma segura y se entregue puntualmente.",
    about_ops_p3: "Hexxium Research se dedica a ser un socio confiable y digno de confianza en investigación. Nos enfocamos en la consistencia de nuestros compuestos, la precisión en nuestra logística y la transparencia en nuestra documentación. Desde el abastecimiento hasta el cumplimiento, cada paso refleja nuestro compromiso de mantener altos estándares y credibilidad a largo plazo.",
    about_closing: "Hexxium Research existe para apoyar la investigación científica responsable y contribuir al avance de soluciones de salud impulsadas por la investigación. Nuestro compromiso se extiende más allá de los compuestos que proporcionamos. Es un compromiso con los investigadores y laboratorios que dependen de la consistencia, transparencia, eficiencia y excelencia.",
    about_explore_catalog: "Explorar Nuestro Catálogo",

    // Cart Page
    cart_title: "Carrito de Compras",
    cart_empty: "Su carrito está vacío",
    cart_items_count: "artículo",
    cart_items_count_plural: "artículos",
    cart_in_your_cart: "en su carrito",
    cart_empty_title: "Su carrito está vacío",
    cart_empty_desc: "Explore nuestro catálogo de péptidos de grado investigación y agregue productos a su carrito.",
    cart_browse: "Explorar Productos",
    cart_batch: "Lote:",
    cart_clear: "Vaciar Carrito",
    cart_each: "c/u",
    cart_order_summary: "Resumen del Pedido",
    cart_subtotal: "Subtotal",
    cart_shipping: "Envío",
    cart_shipping_calc: "Calculado al finalizar",
    cart_total: "Total",
    cart_checkout: "Proceder al Pago",
    cart_secure_note: "Pago seguro con cifrado estándar de la industria",
    cart_continue: "Seguir Comprando",

    // Login Page
    login_welcome: "Bienvenido de Nuevo",
    login_subtitle: "Inicie sesión para acceder a su cuenta de investigación",
    login_email_label: "Correo Electrónico",
    login_password_label: "Contraseña",
    login_forgot: "¿Olvidó su contraseña?",
    login_submit: "Iniciar Sesión",
    login_new_user: "¿Nuevo en Hexxium?",
    login_create_account: "Crear Cuenta de Investigación",
    login_admin_hint: "¿Admin? Use admin@helix.com para acceder al panel de administración.",

    // Register Page
    register_community_title_1: "Únase a la",
    register_community_title_2: "Comunidad de Investigación",
    register_community_desc: "Cree una cuenta gratuita para acceder a nuestro catálogo completo de péptidos de grado investigación y documentación.",
    register_benefit_1: "Acceso al catálogo completo de productos",
    register_benefit_2: "Verificación de lotes y descargas COA",
    register_benefit_3: "Seguimiento de pedidos e historial",
    register_benefit_4: "Guardar productos favoritos",
    register_benefit_5: "Acceso a la biblioteca de investigación",
    register_research_note_title: "Solo para Investigación:",
    register_research_note: "Al crear una cuenta, usted confirma que es un investigador calificado o está afiliado a una institución de investigación. Todos los productos están destinados únicamente para fines de investigación en laboratorio.",
    register_title: "Crear Cuenta",
    register_subtitle: "Complete sus datos para comenzar",
    register_name_label: "Nombre Completo",
    register_email_label: "Correo Electrónico",
    register_password_label: "Contraseña",
    register_confirm_label: "Confirmar Contraseña",
    register_terms_pre: "Acepto los",
    register_terms_of_service: "Términos de Servicio",
    register_terms_and: "y la",
    register_privacy_policy: "Política de Privacidad",
    register_terms_post: ", y confirmo que todas las compras son solo para fines de investigación.",
    register_submit: "Crear Cuenta",
    register_have_account: "¿Ya tiene una cuenta?",
    register_sign_in: "Iniciar Sesión",

    // Footer
    footer_desc: "Compuestos de investigación de calidad premium. Probados por terceros con total transparencia de lotes y documentación COA.",
    footer_newsletter_placeholder: "Ingrese su correo",
    footer_shop_title: "Tienda",
    footer_shop_all: "Todos los Productos",
    footer_shop_regenerative: "Regenerativos",
    footer_shop_growth: "Factores de Crecimiento",
    footer_shop_skin: "Investigación de Piel",
    footer_research_title: "Investigación",
    footer_research_library: "Biblioteca de Investigación",
    footer_research_batch: "Verificación de Lotes",
    footer_research_coa: "Documentación COA",
    footer_account_title: "Cuenta",
    footer_account_login: "Iniciar Sesión",
    footer_account_register: "Registrarse",
    footer_account_dashboard: "Panel",
    footer_account_orders: "Historial de Pedidos",
    footer_disclaimer_title: "Solo para Uso en Investigación",
    footer_disclaimer_text: "Todos los productos vendidos por Hexxium Research se destinan exclusivamente para investigación de laboratorio in-vitro y preclínica. Al comprar, usted confirma que es un investigador calificado o está afiliado a una institución de investigación.",
    footer_copyright: "Hexxium Research. Todos los derechos reservados.",
    footer_terms: "Términos de Servicio",
    footer_privacy: "Política de Privacidad",
    footer_refund: "Política de Reembolso",
    footer_research_only: "Solo para Investigación",

    // Contact Page
    contact_badge: "Contáctenos",
    contact_title_1: "Contáct",
    contact_title_2: "enos",
    contact_subtitle: "¿Tiene alguna consulta? No dude en contactarnos. Nos encantaría saber de usted.",
    contact_form_title: "Envíenos un Mensaje",
    contact_first_name: "Nombre",
    contact_last_name: "Apellido",
    contact_email: "Correo Electrónico",
    contact_phone: "Número de Teléfono",
    contact_message: "Mensaje",
    contact_message_placeholder: "¿Cómo podemos ayudarle?",
    contact_submit: "Enviar Mensaje",
    contact_sending: "Enviando...",
    contact_success: "¡Mensaje enviado exitosamente! Nos pondremos en contacto pronto.",
    contact_error: "Error al enviar el mensaje. Por favor intente nuevamente.",
    contact_info_title: "Información de Contacto",
    contact_info_email: "Correo Electrónico",
    contact_info_response: "Tiempo de Respuesta",
    contact_info_response_value: "Dentro de 24 horas",
    contact_info_research: "Consultas de Investigación",
    contact_info_research_value: "Preguntas sobre productos y lotes bienvenidas",

    // Research page
    research_badge: "Centro de Conocimiento de Péptidos",
    research_title: "Biblioteca de",
    research_title_2: "Investigación",
    research_subtitle: "Explore nuestra base de datos integral de péptidos basada en investigación. Busque por nombre, mecanismo, objetivo o categoría.",
    research_search_placeholder: "Buscar cualquier péptido — escriba un nombre y presione Enter para resultados con IA...",
    research_tab_featured: "Péptidos Destacados",
    research_tab_categories: "Explorar por Categoría",
    research_tab_learn: "Comienza Aquí",
    research_tab_all: "Todos los Péptidos",
    research_featured_title: "Péptidos Destacados",
    research_featured_subtitle: "Los péptidos más estudiados e investigados en la ciencia moderna.",
    research_categories_title: "Explorar por Categoría",
    research_categories_subtitle: "Explore péptidos organizados por su aplicación principal de investigación.",
    research_learn_title: "Comienza Aquí",
    research_learn_subtitle: "¿Nuevo en la investigación de péptidos? Comience con estos temas fundamentales.",
    research_all_title: "Todos los Péptidos",
    research_all_count: "péptidos en la base de datos",
    research_all_filter: "Todos",
    research_view_profile: "Ver perfil completo",
    research_view_profile_short: "Ver perfil",
    research_no_results: "Sin resultados en nuestra base de datos para",
    research_ai_search: "Buscar con IA",
    research_ai_search_desc: "Obtener información de investigación con IA sobre",
    research_ai_search_full: "Buscar con Asistente de Investigación IA",
    research_ai_enter_hint: "O presione Enter para buscar con IA",
    research_ai_loading: "Investigando con IA...",
    research_ai_result: "Resultado de Investigación IA",
    research_ai_disclaimer: "Contenido de investigación generado por IA. Solo con fines educativos. No es consejo médico.",
    research_disclaimer_title: "Aviso Legal de Investigación y Educación",
    research_disclaimer_text: "Toda la información presentada en la Biblioteca de Investigación de Hexxium es solo con fines educativos y de investigación. El contenido proviene de literatura científica publicada y no pretende ser consejo médico, diagnóstico o recomendaciones de tratamiento. Los péptidos discutidos son solo para uso en laboratorio e investigación. Siempre consulte con profesionales de salud calificados para decisiones relacionadas con la salud.",
    research_disclaimer_source: "Basado en literatura de investigación revisada por pares y estudios científicos publicados.",
    research_peptide_count: "péptido",
    research_peptide_count_plural: "péptidos",

    // Peptide Profile
    profile_overview: "Descripción General",
    profile_origin: "Origen",
    profile_mechanism: "Mecanismo de Acción",
    profile_applications: "Aplicaciones Potenciales de Investigación",
    profile_administration: "Vías de Administración",
    profile_safety: "Consideraciones de Seguridad",
    profile_storage: "Notas de Almacenamiento",
    profile_cycling: "Descripción del Ciclado",
    profile_stacking: "Contexto de Combinación",
    profile_disclaimer: "Solo con fines de investigación y educación. No es consejo médico.",

    // AI Assistant
    assistant_title: "Asistente de Investigación",
    assistant_ask: "Haz una pregunta sobre péptidos",
    assistant_placeholder: "Pregunta sobre péptidos...",
    assistant_disclaimer: "Solo con fines educativos. No es consejo médico.",
    assistant_error: "Lo siento, no pude procesar tu consulta. Intenta de nuevo.",
    assistant_suggestions: ["¿Qué es BPC-157?", "¿Péptidos para recuperación?", "¿Cómo funciona Epitalon?"],

    // Calculator
    calc_badge: "Herramienta Inteligente de Investigación",
    calc_title_1: "Calculadora de",
    calc_title_2: "Péptidos",
    calc_subtitle: "Calculadora inteligente de reconstitución con datos de investigación específicos de péptidos, validación de dosis y recomendaciones inteligentes.",

    // Common
    loading: "Cargando...",
    error: "Error",
    close: "Cerrar",
    back: "Atrás",
    next: "Siguiente",
    submit: "Enviar",
    save: "Guardar",
    cancel: "Cancelar",
    search: "Buscar",
    filter: "Filtrar",
    sort: "Ordenar",
    price: "Precio",
    quantity: "Cantidad",
    total: "Total",
  }
};

export function LangProvider({ children }) {
  const [lang, setLang] = useState(() => localStorage.getItem("hexxium_lang") || "en");

  useEffect(() => {
    localStorage.setItem("hexxium_lang", lang);
  }, [lang]);

  const t = (key) => translations[lang]?.[key] || translations.en[key] || key;
  const toggle = () => setLang(lang === "en" ? "es" : "en");

  return (
    <LangContext.Provider value={{ lang, setLang, t, toggle }}>
      {children}
    </LangContext.Provider>
  );
}

export function useLang() {
  return useContext(LangContext);
}
